import { createEnStatusValueForClaim } from '../../../utils/createStatusForClaims'

interface PreparePayloadParams {
  values: any
  initialValues: any
  selectedClient: any
  platformId: number
  startDateString: string
  finishDateString: string
  timeStart: Date | null
  timeFinish: Date | null
  getFormatDate: (time: Date | null) => string | null
}

export const prepareSubmitPayload = ({
  values,
  initialValues,
  selectedClient,
  platformId,
  startDateString,
  finishDateString,
  timeStart,
  timeFinish,
  getFormatDate,
}: PreparePayloadParams) => {
  let changedFields: any = {}

  Object.keys(values).forEach((key) => {
    if (values[key] !== initialValues[key]) {
      changedFields[key] = values[key]
    }
  })

  const currentClientId = String(selectedClient || values.clientId || '')
  const initialClientId = String(initialValues.clientId || '')

  if (currentClientId !== initialClientId) {
    changedFields.clientId = currentClientId
  }

  const currentPlatformId = String(platformId || values.platformId || '')
  const initialPlatformId = String(initialValues.platformId || '')

  if (currentPlatformId !== initialPlatformId && currentPlatformId !== '-1') {
    changedFields.platformId = currentPlatformId
  }

  const initialStartDateString = initialValues.dateStart || ''
  const initialStartTimeString = initialValues.timeStart || ''
  const currentStartDateString = startDateString || values.dateStart || ''
  const currentStartTimeString = timeStart ? getFormatDate(timeStart) : values.timeStart || ''

  if (
    currentStartDateString !== initialStartDateString ||
    currentStartTimeString !== initialStartTimeString
  ) {
    changedFields.dateStart = currentStartDateString
    changedFields.timeStart = currentStartTimeString
  }

  const initialFinishDateString = initialValues.dateFinish || ''
  const initialFinishTimeString = initialValues.timeFinish || ''
  const currentFinishDateString = finishDateString || values.dateFinish || ''
  const currentFinishTimeString = timeFinish ? getFormatDate(timeFinish) : values.timeFinish || ''

  if (
    currentFinishDateString !== initialFinishDateString ||
    currentFinishTimeString !== initialFinishTimeString
  ) {
    changedFields.dateFinish = currentFinishDateString
    changedFields.timeFinish = currentFinishTimeString
  }

  if (values.discountType === 'В процентах') {
    changedFields.discountPercent = values.discountPercent
    changedFields.discountAmount = 0
  } else if (values.discountType === 'В рублях') {
    changedFields.discountAmount = values.discountAmount
    changedFields.discountPercent = 0
  }

  if (values.serviceFeeType === 'В процентах') {
    changedFields.serviceFeePercent = values.serviceFeePercent
    changedFields.serviceFeeAmount = 0
  } else if (values.serviceFeeType === 'В рублях') {
    changedFields.serviceFeeAmount = values.serviceFeeAmount
    changedFields.serviceFeePercent = 0
  }

  if (values.corkFeeType === 'В процентах') {
    changedFields.corkFeePercent = values.corkFeePercent
    changedFields.corkFeeAmount = 0
  } else if (values.corkFeeType === 'В рублях') {
    changedFields.corkFeeAmount = values.corkFeeAmount
    changedFields.corkFeePercent = 0
  }

  changedFields.paymentType = values.paymentType
  changedFields.paymentStatus = values.paymentStatus
  changedFields.prepaymentAmount = Number(values.prepaymentAmount || 0)

  if (values.paymentStatus === 'Оплачено') {
    changedFields.paid = Number(values.price || 0)
  } else {
    changedFields.paid = 0
  }

  const payload = { ...changedFields }

  delete payload.dateStart
  delete payload.dateFinish
  delete payload.timeFinish
  delete payload.timeStart

  if (changedFields.platformId) {
    payload.placeId = String(changedFields.platformId)
  }

  if (changedFields.dateStart || changedFields.timeStart) {
    const dateToUse = changedFields.dateStart || startDateString || values.dateStart
    const timeToUse =
      changedFields.timeStart || (timeStart ? getFormatDate(timeStart) : values.timeStart)
    if (dateToUse && timeToUse) {
      payload.startDate = `${dateToUse.split('.').reverse().join('-')} ${timeToUse}`
    }
  }

  if (changedFields.dateFinish || changedFields.timeFinish) {
    const dateToUse = changedFields.dateFinish || finishDateString || values.dateFinish
    const timeToUse =
      changedFields.timeFinish || (timeFinish ? getFormatDate(timeFinish) : values.timeFinish)
    if (dateToUse && timeToUse) {
      payload.endDate = `${dateToUse.split('.').reverse().join('-')} ${timeToUse}`
    }
  }

  if (changedFields.status) {
    payload.status = createEnStatusValueForClaim(payload.status)
  }

  if (changedFields.peopleCount) {
    payload.peopleCount = String(changedFields.peopleCount)
  }

  if (changedFields.price) {
    payload.price = Number(changedFields.price)
  }

  if (changedFields.clientId) {
    payload.client = Number(changedFields.clientId)
  }

  if (Object.prototype.hasOwnProperty.call(changedFields, 'services')) {
    const cleanedServices = (values.services || [])
      .map((s: any) => ({
        name: (s?.name || '').trim(),
        comment: (s?.comment || '').trim(),
        template: s?.template || null,
      }))
      .filter((s: any) => s.name.length > 0 || s.comment.length > 0)

    if (cleanedServices.length > 0) {
      payload.services = cleanedServices
    }
  }

  if (Object.prototype.hasOwnProperty.call(changedFields, 'events')) {
    const cleanedEvents = (values.events || [])
      .map((e: any) => {
        const timeStart = (e?.timeStart || '').trim()
        const timeEnd = (e?.timeEnd || '').trim()
        let timeString = ''
        if (timeStart && timeEnd) {
          timeString = `${timeStart} — ${timeEnd}`
        } else if (timeStart) {
          timeString = timeStart
        }
        return {
          name: (e?.name || '').trim(),
          comment: (e?.comment || '').trim(),
          time: timeString,
        }
      })
      .filter((e: any) => e.name.length > 0 || e.comment.length > 0)

    if (cleanedEvents.length > 0) {
      payload.events = cleanedEvents
    }
  }

  return payload
}
